import { type Links_userProfile$key } from '#app/__generated__/relay/Links_userProfile.graphql.js';
import { type LinksUpdateExternalLinksMutation } from '#app/__generated__/relay/LinksUpdateExternalLinksMutation.graphql.js';
import { EditSocialLinks } from '#app/components/EditSocialLinks/EditSocialLinks.js';
import { type LinksToEdit } from '#app/components/EditSocialLinks/types.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { InlineEditingEmptyPlaceholder } from './InlineEditingEmptyPlaceholder.js';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { useCallback, useState } from 'react';

type LinksProps = {
  readonly disableEditing?: boolean;
  readonly userProfileRef: Links_userProfile$key;
};

export const Links = ({
  disableEditing = false,
  userProfileRef,
}: LinksProps) => {
  const toast = useToast();

  const profile = useFragment<Links_userProfile$key>(
    graphql`
      fragment Links_userProfile on UserProfile {
        id
        visitorCanEdit

        externalLinks {
          edges {
            node {
              id
              label
              url
            }
          }
        }

        ...ProfileExternalLink_userProfile
      }
    `,
    userProfileRef,
  );

  const [updateLinks] = useContraMutation<LinksUpdateExternalLinksMutation>(
    graphql`
      mutation LinksUpdateExternalLinksMutation(
        $input: UpdateUserProfileInput!
      ) {
        updateUserProfile(input: $input) {
          errors {
            __typename
            message
          }

          userAccount {
            profile {
              externalLinks {
                count
                edges {
                  node {
                    id
                    label
                    url
                  }
                }
              }
            }
          }
        }
      }
    `,
  );

  const onLinksChange = useCallback(
    (updatedLinks: LinksToEdit) => {
      updateLinks({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            toast.success('Links updated');
          }
        },
        onError: () => {
          toast.error('An unexpected error occurred. Please try again.');
        },
        variables: {
          input: {
            externalLinks: updatedLinks.map(({ label, url }) => ({
              label,
              url,
            })),
          },
        },
      });
    },
    [toast, updateLinks],
  );

  const { externalLinks: cachedExternalLinks } = profile;

  const transformedLinks = cachedExternalLinks.edges.map(
    ({ node: { id, label, url } }) => ({
      id,
      label,
      url,
    }),
  );

  const [externalLinks, setExternalLinks] =
    useState<LinksToEdit>(transformedLinks);

  const { visitorCanEdit } = profile;

  const { isModalOpen: isAddLinksOpen, openModal: openAddLinks } = useModal({
    identifier: 'EditSocialLinksModal',
    modalProps: {
      links: externalLinks,
      onLinksChange,
      updateLinks: setExternalLinks,
      userProfileRef: profile,
    },
    staticAdditionalTrackingProperties: {
      section: 'User Profile Links',
    },
  });

  if (visitorCanEdit && !disableEditing && externalLinks.length === 0) {
    return (
      <InlineEditingEmptyPlaceholder
        label="Add social links"
        onClick={openAddLinks}
      />
    );
  }

  return (
    <InlineEditContainer
      label="Update social links"
      onClick={() => {
        if (visitorCanEdit) {
          openAddLinks();
        }
      }}
      visitorCanEdit={visitorCanEdit && !disableEditing}
    >
      <EditSocialLinks
        isAddLinksOpen={isAddLinksOpen}
        isEditingAll={false}
        openAddLinks={openAddLinks}
        socialLinks={externalLinks}
        visitorCanEdit={visitorCanEdit}
      />

      {visitorCanEdit && !disableEditing ? <EditIcon size={16} /> : null}
    </InlineEditContainer>
  );
};
